import { isEmpty } from 'lodash';
import styled from 'styled-components';
import React, { useContext, useEffect, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useLocation, useParams, Outlet } from 'react-router-dom';

import Body2 from '@joinforma/ui/components/Body2';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import ExpiringFundsBanner from 'components/common/ExpiringFundsBanner';
import AccountSelect from 'components/common/AccountSelect';
import CategorySelect from 'components/common/CategorySelect';
import SearchBox from 'components/common/SearchBox';
import {
  FeatureFlag as FLAGS,
  EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS,
} from 'constants/featureFlag';
import { BannerType } from 'constants/products';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import PretaxContext from 'contexts/PretaxContext';
import { PretaxWallet } from 'contexts/PretaxContext/types';
import useQueryCategoriesWithProducts from 'hooks/useQueryCategoriesWithProducts';
import {
  allowMarketplace,
  getActiveEmployeeWallets,
  selectDefaultWallet,
} from 'utils/employee';
import { displayHsaBulkTransferBannersOnStore } from 'utils/hsaBulkTransfer';
import {
  filterActiveAlegeusPretaxAccountsWithFsaType,
  filterActiveFormaPretaxAccounts,
  hasFsaLpf,
} from 'utils/pretaxAccount';
import { getExpiringFundsTotalAndDays, getWalletsExpiring } from 'utils/wallet';

import * as types from './types';

const SEARCH_PATH = '/search';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  max-width: 1160px;
  margin: 22px auto;
  padding: 0 20px;
  gap: 20px;

  @media (min-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    flex-direction: row;
    justify-content: space-between;
  }

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    padding: 0;
  }
`;

const DropdownsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: ${ScreenWidth.MOBILE_PORTRAIT}) {
    flex-direction: row;
    align-items: center;
    max-width: 400px;
  }
`;

const DropdownsLabel = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SearchContainer = styled.div`
  display: flex;
  width: 100%;

  @media (min-width: ${ScreenWidth.MOBILE_PORTRAIT}) {
    align-self: flex-end;
    width: auto;
  }
`;

// this is a fake border since adding a border-bottom to the container doesn't seme to work
const Border = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.background.stroke};
`;

const StoreLandingCompactLayout = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { company, employee } = useContext(CurrentUserContext);
  const ff = useContext(FeatureFlagContext);
  const {
    alegeus_pretax_accounts: alegeusPretaxAccounts,
    hsaBulkTransferData,
    new_pretax_experience: newPretaxExp,
    pretax_accounts: formaPretaxAccounts,
  } = useContext(PretaxContext);
  const location = useLocation();
  const handleError = useErrorHandler();
  const { employee_wallet_id: walletId } = useParams() as {
    employee_wallet_id: string;
  };

  const expiringFundsFlag = ff.flag(FLAGS.EXPIRING_FUNDS_BANNER_V2, 'off');
  const alegeusMigrated = ff.flag(FLAGS.ALEGEUS_MIGRATED_CUSTOMER, false);

  const initialQuery = new URLSearchParams(location.search).get('q') ?? '';

  const [searchQuery, setSearchQuery] = React.useState(initialQuery);
  useEffect(() => {
    if (location.pathname !== SEARCH_PATH) {
      setSearchQuery('');
    }
  }, [location.pathname]);

  const {
    employee_wallets: wallets,
    settings: { country, currency, is_posttax_enabled: posttaxEnabled },
  } = employee;

  const activeWallets = useMemo(() => {
    if (!allowMarketplace(company)) {
      return [];
    }
    const { company_wallet_configurations } = company;
    return getActiveEmployeeWallets({
      company_wallet_configurations,
      employer_sponsored_wallets: wallets,
      store_enabled: true,
    });
  }, [company, wallets]);

  const filteredWallet = walletId
    ? activeWallets.find(({ id }) => id === walletId)
    : selectDefaultWallet({ employee_wallets: activeWallets });

  const expiringWallets = getWalletsExpiring(activeWallets);

  const {
    expiring_funds_days_left: expiringDaysLeft,
    expiring_funds_total: expiringTotal,
  } = getExpiringFundsTotalAndDays(expiringWallets);

  const { data: categoriesData } = useQueryCategoriesWithProducts({
    employee_wallets: activeWallets,
    country,
    onError: (error: Error) => handleError(error),
  });
  const categories = allowMarketplace(company) ? categoriesData ?? [] : [];

  const hasFormaPretax = useMemo(
    () => alegeusMigrated && newPretaxExp,
    [alegeusMigrated, newPretaxExp]
  );
  const allPretaxAccounts = useMemo(
    () => (hasFormaPretax ? formaPretaxAccounts : alegeusPretaxAccounts),
    [hasFormaPretax, alegeusPretaxAccounts, formaPretaxAccounts]
  );

  const pretaxAccounts = hasFormaPretax
    ? filterActiveFormaPretaxAccounts({
        pretax_accounts: allPretaxAccounts as Array<PretaxWallet>,
      })
    : filterActiveAlegeusPretaxAccountsWithFsaType({
        pretax_accounts: allPretaxAccounts as Array<types.PretaxAccount>,
      });

  const showExpiringFundsBanner =
    expiringFundsFlag &&
    EXPIRING_FUNDS_ON_CONTROL_BANNER_SEGMENTS.includes(expiringFundsFlag) &&
    expiringTotal > 0 &&
    expiringDaysLeft <= 10;
  const showSearch = posttaxEnabled && !isEmpty(activeWallets);

  return (
    <Container>
      {displayHsaBulkTransferBannersOnStore(hsaBulkTransferData)}
      {showExpiringFundsBanner && (
        <ExpiringFundsBanner
          currency={currency}
          days_left={expiringDaysLeft}
          total={expiringTotal}
          wallets_expiring={expiringWallets}
          variation={expiringFundsFlag}
          banner_type={BannerType.STORE}
        />
      )}
      <ContentContainer>
        <DropdownsContainer>
          <DropdownsLabel>Browse</DropdownsLabel>
          {filteredWallet?.company_wallet_configuration && (
            <CategorySelect
              categories={categories}
              filteredWallet={filteredWallet}
              label="Categories"
            />
          )}
          <AccountSelect
            eligibleWallets={activeWallets}
            hasFsaLpf={hasFsaLpf({ hasFormaPretax, pretaxAccounts })}
            label="Accounts"
            isFormaPretax={hasFormaPretax}
            pretaxAccounts={pretaxAccounts}
          />
        </DropdownsContainer>
        {showSearch && (
          <SearchContainer>
            <SearchBox
              label="Search for an item and hit enter"
              query={searchQuery}
              setSearchQuery={setSearchQuery}
              width={451}
            />
          </SearchContainer>
        )}
      </ContentContainer>
      <Border></Border>
      {children ?? <Outlet />}
    </Container>
  );
};

export default StoreLandingCompactLayout;
